import React, { useState } from 'react'
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles'

import MenuOpenIcon from '@material-ui/icons/MenuOpen'

import { useLocation } from 'react-router-dom'
import TopNavBar, { ProductView } from '../TopBar'
import { AdminPanel } from '../../../components/AdminPanel/AdminPanel'
import { useAppDispatch, useUser } from '../../../hooks'
import { isAccountManager } from '../../../helpers/stringHelpers'
import { Lori } from '../Lori'
import { AGENT_DRAWER_WIDTH, getSidebarWidth } from '../../constants'
import SideBar from './sidebar'
import { APARTMENT } from '../../../helpers'
import { useSelector } from 'react-redux'
import { RootState, setLoriChannel } from '../../../store'
import { useTrainingVideo } from '../../../hooks/useTrainingVideo'
import { useProjectPlan } from '../../../hooks/useProjectPlan'
import {
    useVendorInvoiceState,
    VendorInvoiceContext,
} from '../../../contexts/useVendorInvoice'

interface AuthLayoutProps {
    children: React.ReactNode
}

export const AuthLayout: React.FC<AuthLayoutProps> = ({ children }) => {
    const location = useLocation()

    const { rootUser, workspaceUser } = useUser()

    const sidebarWidth = getSidebarWidth(
        workspaceUser?.active_workspace.company_type,
    )

    const trainingVideoController = useTrainingVideo()
    const projectPlanController = useProjectPlan()

    const isRegistrationPage =
        location.pathname === '/vendor-register' ||
        location.pathname === '/skillcat-register'

    const isFullWidthPage =
        location.pathname === '/ez-chat-final' || isRegistrationPage

    const selectedChannel = useSelector(
        (state: RootState) => state.user.selectedLoriChannel,
    )

    const isLoriDrawerOpen =
        selectedChannel !== 'none' &&
        workspaceUser?.active_workspace.company_type === APARTMENT

    const contentAreaWidth = isLoriDrawerOpen
        ? `calc(100vw - ${AGENT_DRAWER_WIDTH}px - ${sidebarWidth}px)`
        : `calc(100vw - ${sidebarWidth}px)`

    const useStyles = makeStyles((theme: Theme) =>
        createStyles({
            root: {
                display: 'flex',
                width: '100%',
            },
            drawer: {
                flexShrink: 0,
                whiteSpace: 'nowrap',
                backgroundColor: '#2f2e41',
                color: '#737378',
                zIndex: 9999,
            },
            drawerOpen: {
                width: AGENT_DRAWER_WIDTH,
                transition: theme.transitions.create('width', {
                    easing: theme.transitions.easing.sharp,
                    duration: theme.transitions.duration.enteringScreen,
                }),
                backgroundColor: '#2f2e41',
                color: '#737378',
            },
            drawerClose: {
                transition: theme.transitions.create('width', {
                    easing: theme.transitions.easing.sharp,
                    duration: theme.transitions.duration.leavingScreen,
                }),
                overflowX: 'hidden',
                width: `${AGENT_DRAWER_WIDTH} !important`,
                [theme.breakpoints.up('sm')]: {
                    width: `${AGENT_DRAWER_WIDTH} !important`,
                },
                backgroundColor: '#2f2e41',
                color: '#737378',
            },
            toolbar: {
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-end',
                padding: theme.spacing(0, 1),
                minHeight: '56px !important',
                ...theme.mixins.toolbar,
            },
            contentDrawerOpen: {
                flexGrow: 1,
                transition: theme.transitions.create('width', {
                    easing: theme.transitions.easing.sharp,
                    duration: theme.transitions.duration.enteringScreen,
                }),
            },
            contentDrawerClose: {
                flexGrow: 1,
                transition: theme.transitions.create('margin', {
                    easing: theme.transitions.easing.sharp,
                    duration: theme.transitions.duration.leavingScreen,
                }),
            },
        }),
    )
    const classes = useStyles()

    const [adminDrawerOpen, setAdminDrawerOpen] = useState(false)

    const dispatch = useAppDispatch()

    const vendorInvoiceState = useVendorInvoiceState()

    const handleLoriDrawerToggle = () => {
        dispatch(setLoriChannel('none'))
    }

    const setSelectedChannel = (channel: LoriChannel) => {
        dispatch(setLoriChannel(channel))
    }

    return (
        <div className={classes.root}>
            {!isRegistrationPage && (
                <>
                    <TopNavBar
                        loriOpen={
                            selectedChannel !== 'none' &&
                            workspaceUser?.active_workspace.company_type ===
                                APARTMENT
                        }
                        loriDrawerWidth={sidebarWidth}
                        productView={ProductView.EZTURN}
                        open={true}
                    />
                    <SideBar
                        open
                        drawerWidth={sidebarWidth}
                        selectedChannel={selectedChannel}
                        setSelectedChannel={setSelectedChannel}
                    />
                    <Lori
                        open={
                            selectedChannel !== 'none' &&
                            workspaceUser?.active_workspace.company_type ===
                                APARTMENT
                        }
                        onToggleDrawer={handleLoriDrawerToggle}
                        selectedChannel={selectedChannel}
                        setSelectedChannel={setSelectedChannel}
                        drawerWidth={AGENT_DRAWER_WIDTH}
                        trainingVideoController={trainingVideoController}
                        projectPlanController={projectPlanController}
                    />
                </>
            )}

            <main
                className={
                    isLoriDrawerOpen
                        ? classes.contentDrawerOpen
                        : classes.contentDrawerClose
                }
                style={{
                    padding: isFullWidthPage ? 0 : '24px',
                    width: contentAreaWidth,
                }}
            >
                <div
                    className={classes.toolbar}
                    style={{
                        minHeight: isRegistrationPage ? 0 : '56px',
                    }}
                />
                <VendorInvoiceContext.Provider value={vendorInvoiceState}>
                    {children}
                </VendorInvoiceContext.Provider>
            </main>

            {isAccountManager(rootUser) && (
                <div>
                    <div
                        style={{
                            height: 32,
                            width: 32,
                            position: 'fixed',
                            bottom: 2,
                            right: 2,
                            backgroundColor: '#2F2E41',
                            borderRadius: 32,
                            zIndex: 1000000000000,
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            cursor: 'pointer',
                        }}
                        onClick={() => setAdminDrawerOpen((open) => !open)}
                    >
                        <MenuOpenIcon htmlColor="#fff" />
                    </div>

                    <AdminPanel
                        open={adminDrawerOpen}
                        onClose={() => setAdminDrawerOpen(false)}
                    />
                </div>
            )}
        </div>
    )
}
